/**
 * identifiers - "is this string for a machine or for a person?"
 *
 * Ids leak into UI text whenever a display path falls back to a slug: a
 * drafted document with no name printed its `document_type_id`, and for a
 * draft built on a user's own structure template that id is the synthetic
 * `template_<uuid>`. The result is a raw uuid rendered as a document's name,
 * in the page title, in the meta line and on every activity row.
 *
 * The rule for telling the two apart is small but it is subtle - it has to
 * reject uuids and row ids while leaving `nda`, `deed` and `sha` alone - so it
 * lives in one neutral place that both the drafting surfaces and the shared
 * activity model can read. Neither has to depend on the other to agree.
 */

/** A v4-ish uuid. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A bare row id - `template_417`. */
const NUMERIC_RE = /^\d+$/;

/**
 * Is this token an identifier rather than a word?
 *
 * Deliberately narrow. The long-hex arm requires 16+ chars AND at least one
 * digit AND nothing but hex/dashes, so no real legal-document word reaches it:
 * the longest all-hex English words ("defaced", "effaced") are seven letters
 * and carry no digit.
 */
export const isOpaqueId = (token: string | null | undefined): boolean => {
  const s = (token || '').trim();
  if (!s) return false;
  if (UUID_RE.test(s)) return true;
  if (NUMERIC_RE.test(s)) return true;
  return s.length >= 16 && /^[0-9a-f-]+$/i.test(s) && /\d/.test(s);
};

export default isOpaqueId;
